/**
 * The four walls standing around the discard field.
 *
 * Every stack is one span. The two tiles in it are the span's own ::before and
 * ::after, so a full 136-tile wall costs 68 elements instead of 204 and can be
 * repainted without touching the DOM: `data-upper` / `data-lower` flip, CSS
 * does the rest.
 *
 * The component is memoised on the counts it draws from, because the wall only
 * ever changes when a tile leaves it - not when a bot thinks, a claim window
 * opens, or the language changes.
 *
 * It shows no tile faces, carries no tile ids and receives no seed: see
 * `wallPresentation.ts` for why the picture cannot leak anything.
 */

import { memo } from "react";
import { presentWall } from "../game/wallPresentation";
import type { WallSideName } from "../game/wallPresentation";

export interface TableWallProps {
  wallRemaining: number;
  deadWallRemaining: number;
  dealer: number;
  /** The seat this screen belongs to, so the break is drawn in front of the dealer. */
  you: number;
  /** Dims the wall while the opening ceremony builds its own. */
  hidden?: boolean;
}

/** The wall for a side, with the break marked so the opening reads as an opening. */
function WallSideRow({
  side,
  stacks,
  breakSide,
  breakIndex,
}: {
  side: WallSideName;
  stacks: readonly { index: number; upper: boolean; lower: boolean }[];
  breakSide: WallSideName;
  breakIndex: number;
}) {
  return (
    <div className={`wall__side wall__side--${side}`} data-side={side}>
      {stacks.map((stack) => {
        const height = (stack.upper ? 1 : 0) + (stack.lower ? 1 : 0);
        return (
          <span
            key={stack.index}
            className="wall__stack"
            data-height={height}
            data-upper={stack.upper ? "true" : "false"}
            data-lower={stack.lower ? "true" : "false"}
            {...(side === breakSide && stack.index === breakIndex
              ? { "data-break": "true" }
              : {})}
            style={{ "--stack": stack.index } as React.CSSProperties}
          />
        );
      })}
    </div>
  );
}

export const TableWall = memo(function TableWall({
  wallRemaining,
  deadWallRemaining,
  dealer,
  you,
  hidden = false,
}: TableWallProps) {
  const wall = presentWall({ wallRemaining, deadWallRemaining, dealer, you });

  return (
    <div
      className="wall"
      data-testid="table-wall"
      data-standing={wall.standing}
      data-hidden={hidden ? "true" : "false"}
      aria-hidden="true"
    >
      {wall.sides.map((side) => (
        <WallSideRow
          key={side.side}
          side={side.side}
          stacks={side.stacks}
          breakSide={wall.breakSide}
          breakIndex={wall.breakIndex}
        />
      ))}
    </div>
  );
});
